import { api } from "../../api/index.ts";
import { useCallback } from "react";
import { ContractForm } from "../../types/contracts.ts";
import {
  contractUpdateToExternal,
  contractCreateToExternal,
} from "./convert.ts";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { extractApiError } from "../../api/errors.ts";
import { useShowToast } from "../../contexts/ToastContext.tsx";
import { useAppDispatch } from "../../redux/store.ts";
import { useView } from "../../contexts/ViewContext.tsx";
import { loadingCellsSlice } from "../../redux/slices/loadingCells.ts";

export const useSetContractData = () => {
  const showToast = useShowToast();
  const view = useView();
  const dispatch = useAppDispatch();

  const [triggerCreate] =
    api.endpoints.createContractApiContractsPost.useMutation();
  const [triggerUpdate] =
    api.endpoints.updateContractApiContractsContractIdPut.useMutation();
  const [triggerDelete] =
    api.endpoints.deleteContractApiContractsContractIdDelete.useMutation();

  const setData = useCallback(
    async (updatedRows: Partial<ContractForm>[]) => {
      const loadingCellKeys = updatedRows
        .filter((row) => !!row.id)
        .map((row) =>
          Object.entries(row).reduce(
            (acc, [key]) => [...acc, `${view}.${row.id}.${key}`],
            [] as string[],
          ),
        )
        .flat();
      loadingCellKeys.forEach((key) => {
        dispatch(loadingCellsSlice.actions.cellIsPending({ cell: key }));
      });
      return await Promise.all(
        updatedRows.map((row) => {
          if (row.id) {
            const update = contractUpdateToExternal(row);
            if (Object.entries(update).length !== 0) {
              return triggerUpdate({
                contractId: row.id,
                contractUpdate: update,
              });
            } else {
              return undefined;
            }
          } else {
            return triggerCreate({
              contractCreate: contractCreateToExternal(row as ContractForm),
            });
          }
        }),
      )
        .then((results) => {
          console.info("Update results ", results);
          results.forEach((result) => {
            if (result && "error" in result) {
              showToast({
                severity: "error",
                summary: "Błąd",
                detail: extractApiError(
                  result.error as FetchBaseQueryError | SerializedError,
                ),
              });
            }
          });
          return results;
        })
        .catch((err) => {
          console.error(err);
          return null;
        })
        .finally(() => {
          const runningQueries = dispatch(
            api.util.getRunningQueriesThunk(),
          ).filter((query) =>
            query.queryCacheKey.startsWith("listContractsApiContractsGet"),
          );
          // Wait for the latest running query for list endpoint to finish, this avoids "flickering"
          void Promise.all(runningQueries).finally(() => {
            loadingCellKeys.forEach((key) => {
              dispatch(loadingCellsSlice.actions.cellIsLoaded({ cell: key }));
            });
          });
        });
    },
    [triggerCreate, triggerUpdate, showToast, dispatch, view],
  );

  const deleteData = useCallback(
    async (contractId: number) => {
      try {
        const result = await triggerDelete({ contractId });
        console.info("Deleted contract", contractId, result);
        return result;
      } catch (error) {
        console.error("Delete error", error);
        throw error;
      }
    },
    [triggerDelete],
  );

  return { setData, deleteData };
};
